import type {
  AssistantAnswers,
  AssistantCard,
  AssistantMention,
  AssistantUsage,
  DashboardEdit,
  ExportSpec,
  PlotSpec,
} from '../../api/types.ts';

export const ASSISTANT_HISTORY_KEY = 'neptune_assistant_chats_v1';
export const ASSISTANT_HISTORY_DAYS = 30;
export const ASSISTANT_HISTORY_MAX_CHARS = 1_500_000;
const MAX_CHATS = 100;

export type AssistantToolCall = Record<string, unknown>;

export interface AssistantHistoryTurn {
  role: 'user' | 'assistant';
  content: string;
  /** What the user tagged with `@`, kept so a resumed chat still carries the
   *  context its questions were asked with. */
  mentions?: AssistantMention[];
  reasoning?: string | null;
  plot?: PlotSpec | null;
  export?: ExportSpec | null;
  usage?: AssistantUsage;
  requestId?: string;
  /** Set after the first Apply-to-Plot click was acknowledged. */
  plotApplyClicked?: boolean;
  /** Configuration applied, either automatically or from the proposal button. */
  plotApplied?: boolean;
  exportApplyClicked?: boolean;
  /** A dashboard edit this reply made, and what applying it actually did. */
  dashboardEdit?: DashboardEdit | null;
  dashboardOutcome?: AssistantDashboardOutcome | null;
  /** A proposal lent by another part of the app (`assistant-cards.ts`), or a
   *  question card (`kind: 'clarify'`, `assistant-questions.ts`). */
  card?: AssistantCard | null;
  /** On a user turn: the answers it gave a question card, from the card. */
  answers?: AssistantAnswers | null;
  /** `unsaved`: sent, but the server could not keep it (no stored chat). */
  feedbackState?: 'idle' | 'sending' | 'bad' | 'unsaved';
  toolCalls?: AssistantToolCall[];
  plotAttempts?: AssistantToolCall[];
}

export interface AssistantDashboardOutcome {
  dashboardId: string | null;
  dashboardName: string | null;
  created: boolean;
  done: string[];
  problems: string[];
  /** Set once Undo put the dashboard back. */
  undone?: boolean;
  /** Sent back to the model on the next turn. */
  note: string;
}

export interface AssistantChatHistory {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  turns: AssistantHistoryTurn[];
}

export function newAssistantChatId(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function chatTitle(turns: AssistantHistoryTurn[]): string {
  const first = turns.find((turn) => turn.role === 'user')?.content.trim() ?? '';
  const oneLine = first.replace(/\s+/g, ' ');
  return oneLine.length > 52 ? `${oneLine.slice(0, 51)}…` : oneLine || 'New chat';
}

export function chatUsage(turns: AssistantHistoryTurn[]): {
  input: number;
  output: number;
  cost: number;
} {
  return turns.reduce(
    (total, turn) => {
      if (turn.usage) {
        total.input += turn.usage.input_tokens;
        total.output += turn.usage.output_tokens;
        total.cost += turn.usage.cost_usd ?? 0;
      }
      return total;
    },
    { input: 0, output: 0, cost: 0 },
  );
}

export function browserHistoryCost(chats: AssistantChatHistory[]): number {
  return chats.reduce((total, chat) => total + chatUsage(chat.turns).cost, 0);
}

function validChat(value: unknown): value is AssistantChatHistory {
  if (!value || typeof value !== 'object') return false;
  const chat = value as Partial<AssistantChatHistory>;
  return (
    typeof chat.id === 'string' &&
    typeof chat.title === 'string' &&
    typeof chat.createdAt === 'string' &&
    typeof chat.updatedAt === 'string' &&
    Array.isArray(chat.turns)
  );
}

/** Keep only recent chats, newest first, under a conservative localStorage cap. */
export function pruneAssistantChats(
  chats: AssistantChatHistory[],
  nowMs = Date.now(),
): AssistantChatHistory[] {
  const cutoff = nowMs - ASSISTANT_HISTORY_DAYS * 24 * 60 * 60 * 1000;
  const recent = chats
    .filter((chat) => validChat(chat) && Date.parse(chat.updatedAt) >= cutoff)
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
    .slice(0, MAX_CHATS)
    .map((chat) => ({
      ...chat,
      turns: chat.turns.map((turn) => ({
        ...turn,
        // A reload must never leave a feedback button permanently "Saving…".
        ...(turn.feedbackState === 'sending' ? { feedbackState: 'idle' as const } : {}),
      })),
    }));

  while (recent.length > 1 && JSON.stringify(recent).length > ASSISTANT_HISTORY_MAX_CHARS) {
    recent.pop();
  }
  // A single unusually large chat is still bounded by retaining its newest turns.
  while (
    recent.length === 1 &&
    recent[0]!.turns.length > 2 &&
    JSON.stringify(recent).length > ASSISTANT_HISTORY_MAX_CHARS
  ) {
    recent[0] = { ...recent[0]!, turns: recent[0]!.turns.slice(2) };
  }
  return recent;
}

export function loadAssistantChats(
  storage: Storage = localStorage,
  nowMs = Date.now(),
): AssistantChatHistory[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(ASSISTANT_HISTORY_KEY) ?? '[]');
    const chats = Array.isArray(parsed) ? parsed.filter(validChat) : [];
    const pruned = pruneAssistantChats(chats, nowMs);
    storage.setItem(ASSISTANT_HISTORY_KEY, JSON.stringify(pruned));
    return pruned;
  } catch {
    return [];
  }
}

/** Returns what was actually retained, dropping oldest chats if quota is tighter. */
export function saveAssistantChats(
  chats: AssistantChatHistory[],
  storage: Storage = localStorage,
  nowMs = Date.now(),
): AssistantChatHistory[] {
  const retained = pruneAssistantChats(chats, nowMs);
  while (retained.length) {
    try {
      storage.setItem(ASSISTANT_HISTORY_KEY, JSON.stringify(retained));
      return retained;
    } catch {
      retained.pop();
    }
  }
  try {
    storage.removeItem(ASSISTANT_HISTORY_KEY);
  } catch {
    // Storage can be entirely unavailable (private mode or policy).
  }
  return [];
}
